import { startCase } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import EmployeeAvatar from "../../../components/EmployeeAvatar";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { API_URLS, BASE_URLS } from "../../../core/data/redux/apis/urls";
import Api from "../../../core/data/redux/utils/apiCall";
import { processNocDownloadResponse } from "../../../utils/nocDownloadUtils";
import {
  clearCurrentRequest,
  fetchNOCExIndiaRequest,
  NOCExIndiaRequest,
  fetchTravelList,
  fetchStayList,
} from "../../../core/data/redux/nocExIndiaSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  ChatMessage,
  clearError,
  clearMessages,
  clearUploadError,
  fetchMessages,
  fetchUnreadCount,
  fetchChatAttachments,
  sendMessage,
  uploadChatFiles,
} from "../../../core/data/redux/nocOutsideJobChatSlice";
import {
  openChatAttachment,
  openChatAttachmentInExternalView,
  downloadChatAttachment,
  prepareUploadedAttachments,
  getValidNocType,
  formatChatMessageText,
} from "../../../utils/nocChatUtils";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
} from "../../../utils/fileUtils";
import { handleDocumentClick as handleDocumentClickUtil } from "../../../utils/documentUtils";
import { all_routes } from "../../router/all_routes";

import {
  SapRequestStatus,
  getRequestStatusBadgeClass as getGlobalStatusBadgeClass,
  getRequestStatusLabel as getGlobalStatusLabel,
} from "../../../core/data/constants/requestStatus";

interface ForeignVisitDetailsProps { }

const ForeignVisitDetails = (props: ForeignVisitDetailsProps) => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();

  // Get data from Redux state
  const { currentRequest, loading } = useSelector(
    (state: any) => state.nocExIndiaState
  );

  // Get chat data from Redux state
  const {
    messages,
    attachments,
    loading: chatLoading,
    unreadCount,
    error: chatError,
    uploading,
    uploadError,
  } = useSelector((state: any) => state.nocChat);

  // Local state for the actual application data
  const [applicationData, setApplicationData] =
    useState<NOCExIndiaRequest | null>(null);

  const [currentUserType, setCurrentUserType] = useState<string>("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [isCurrentUserApprover, setIsCurrentUserApprover] = useState<boolean>(false);

  const employeesState = useSelector((state: any) => state.employeesState);

  useEffect(() => {
    dispatch(getMyProfile({}));
    const user = localStorage.getItem("user");
    const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
    setCurrentUserType(userJson?.role || userJson?.user_type || "");
    setCurrentUserId(userJson?.empId || userJson?.empID || "");
  }, [dispatch]);

  useEffect(() => {
    if (applicationData && currentUserId) {
      const appApprover = String(
        (applicationData as any).approverId ||
        (applicationData as any).approverUserID ||
        ""
      ).replace(/^0+/, "");
      const myId = String(currentUserId).replace(/^0+/, "");
      const isApprover =
        appApprover === myId ||
        (applicationData as any).approverId === currentUserId;
      setIsCurrentUserApprover(isApprover);
    }
  }, [applicationData, currentUserId]);

  // State and helpers for NOC download and details
  const [isDownloading, setIsDownloading] = useState(false);
  const [travelList, setTravelList] = useState<any[]>([]);
  const [stayList, setStayList] = useState<any[]>([]);

  useEffect(() => {
    const fetchLists = async () => {
      try {
        const [travelRes, stayRes] = await Promise.all([
          dispatch(fetchTravelList()).unwrap(),
          dispatch(fetchStayList()).unwrap(),
        ]);
        setTravelList(travelRes || []);
        setStayList(stayRes || []);
      } catch (err) {
        console.error("Error loading travel/stay lists in details:", err);
      }
    };
    fetchLists();
  }, [dispatch]);

  const getTravelTitle = (val: string | undefined | null) => {
    if (!val) return "-";
    if (appAny.noc_travel?.travel_title) return appAny.noc_travel.travel_title;
    const match = travelList.find(
      (item: any) =>
        String(item.travel_id).toUpperCase() === String(val).toUpperCase() ||
        String(item.id).toUpperCase() === String(val).toUpperCase()
    );
    if (match?.travel_title) return match.travel_title;

    const fallbackMap: { [key: string]: string } = {
      E: "Self",
      S: "Spouse",
      O: "Others",
      SELF: "Self",
      SPOUSE: "Spouse",
      OTHERS: "Others",
    };
    return fallbackMap[String(val).trim().toUpperCase()] || val;
  };

  const getStayTitle = (val: string | undefined | null) => {
    if (!val) return "-";
    if (appAny.stay_list?.stay_title) return appAny.stay_list.stay_title;
    const match = stayList.find(
      (item: any) =>
        String(item.stay_id).toUpperCase() === String(val).toUpperCase() ||
        String(item.id).toUpperCase() === String(val).toUpperCase()
    );
    if (match?.stay_title) return match.stay_title;

    const fallbackMap: { [key: string]: string } = {
      E: "Self",
      S: "Spouse",
      O: "Others",
      H: "Hotel",
      SELF: "Self",
      SPOUSE: "Spouse",
      OTHERS: "Others",
      HOTEL: "Hotel",
    };
    return fallbackMap[String(val).trim().toUpperCase()] || val;
  };

  const getDarStatusLabel = (code: string | undefined | null) => {
    if (!code) return "-";
    const status = String(code).trim().toUpperCase();
    const mapping: { [key: string]: string } = {
      N: "No D&AR contemplated against the employee",
      P: "D&AR pending",
      M: "D&AR completed; Minor penalty has been imposed",
      J: "D&AR completed; Major penalty has been imposed",
    };
    return mapping[status] || code;
  };

  const handleDownloadPDF = async () => {
    if (!applicationData || isDownloading) return;
    try {
      setIsDownloading(true);
      const payload = {
        empId: applicationData.empID || applicationData.empId,
        type: "ZNEX",
        referenceNumber: applicationData.referenceNumber,
      };

      const res = await Api.post(
        API_URLS.NOC.EX_INDIA_DOWNLOAD,
        payload
      );

      processNocDownloadResponse(res?.data);
    } catch (err: any) {
      console.error("Ex-India NOC Download Error:", err);
      toast.error(err?.message || "Failed to download NOC document.");
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDisplayValue = (val: any): string => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "object") {
      return (
        val.designation_name ||
        val.department_name ||
        val.name ||
        val.title ||
        "-"
      );
    }
    return String(val);
  };

  const profileUser = employeesState?.data?.data;
  const profileDetails = profileUser?.details || profileUser || {};

  const appAny = applicationData as any;
  const empObj =
    appAny?.employee ||
    appAny?.employeeDetails ||
    appAny?.applicantDetails ||
    appAny?.applicant ||
    appAny?.emp_details ||
    appAny?.userDetails ||
    profileUser ||
    {};

  const empDetails = empObj.details || empObj || {};
  const fallbackObj =
    (empDetails.firstname || empDetails.fullName || empDetails.empId)
      ? empDetails
      : profileDetails;

  const sapInfo =
    fallbackObj.sap_department_designation_wings ||
    empDetails.sap_department_designation_wings ||
    profileDetails.sap_department_designation_wings ||
    {};

  const rawEmployeeName =
    (fallbackObj.firstname
      ? `${fallbackObj.firstname} ${fallbackObj.secondname || ""} ${fallbackObj.lsastname || fallbackObj.lastname || ""}`
        .replace(/\s+/g, " ")
        .trim()
      : "") ||
    fallbackObj.fullName ||
    fallbackObj.name ||
    applicationData?.employeeName ||
    appAny?.employeeDetails?.fullName ||
    "";
  const employeeName = formatDisplayValue(rawEmployeeName);

  const rawDesignation =
    sapInfo.designation_name ||
    fallbackObj.designation ||
    fallbackObj.designation_details?.designationName ||
    (fallbackObj.designationID ? `Designation ID: ${fallbackObj.designationID}` : "") ||
    applicationData?.designation ||
    appAny?.employeeDetails?.designation;
  const designation = formatDisplayValue(rawDesignation);

  const rawDepartment =
    sapInfo.department_name ||
    fallbackObj.department_name ||
    fallbackObj.department_details?.departmentName ||
    (fallbackObj.department ? `Department ID: ${fallbackObj.department}` : "") ||
    applicationData?.department ||
    appAny?.employeeDetails?.department;
  const department = formatDisplayValue(rawDepartment);

  const approverName =
    (applicationData as any)?.approverName ||
    appAny?.approverName ||
    appAny?.approver?.name ||
    "";
  const approverId =
    applicationData?.approverUserID ||
    (applicationData as any)?.approverId ||
    appAny?.approverUserID ||
    appAny?.approverId ||
    appAny?.approver?.empId ||
    "";
  const approverDisplay =
    approverName && approverId
      ? `${approverName} (${formatEmpIdForDisplay(approverId)})`
      : approverName
        ? approverName
        : approverId
          ? formatEmpIdForDisplay(approverId)
          : "-";

  const requestStatusStr =
    applicationData?.requestStatus || (applicationData?.is_draft ? "Draft" : "Submitted");

  const isApproved = String(applicationData?.requestStatus || "").toUpperCase() === "D";

  // Chat states
  const [showChat, setShowChat] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messageInputRef = React.useRef<HTMLInputElement>(null);
  const [isSending, setIsSending] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    title: string;
    isImage: boolean;
  } | null>(null);

  // Helper to determine if a message is sent by current logged in user
  const isMyMessage = (msg: ChatMessage): boolean => {
    const userStr = localStorage.getItem("user");
    const userObj = userStr && userStr !== "undefined" ? JSON.parse(userStr) : {};
    const myEmpId = String(userObj?.empId || "").trim().replace(/^0+/, "");
    const senderEmpId = String(msg.senderEmpId || "").trim().replace(/^0+/, "");
    if (myEmpId && senderEmpId) {
      return myEmpId === senderEmpId;
    }
    const isApprover =
      userObj?.role === "APPROVER" ||
      userObj?.user_type === "APPROVER" ||
      Boolean(userObj?.approver_level);
    if (isApprover) {
      return msg.senderType === "approver" || msg.senderType === "admin";
    }
    return msg.senderType === "employee";
  };

  // Auto-scroll to bottom of chat when new messages arrive or when chat is opened
  useEffect(() => {
    if (showChat) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages, showChat]);

  // Load data from Redux state
  useEffect(() => {
    if (id) {
      dispatch(fetchNOCExIndiaRequest({ id: parseInt(id) }));
    }

    return () => {
      dispatch(clearCurrentRequest());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (currentRequest) {
      setApplicationData(currentRequest);
    }
  }, [currentRequest]);

  // Fetch messages and attachments when chat opens (No recurring polling to prevent typing focus loss)
  useEffect(() => {
    if (showChat && applicationData?.id) {
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      dispatch(fetchUnreadCount(applicationData.id));
      dispatch(fetchChatAttachments({ nocRequestId: applicationData.id, nocType: "NOC_EXINDIA" }));
    }
  }, [showChat, applicationData?.id, dispatch]);

  // Fetch unread count on component mount
  useEffect(() => {
    if (applicationData?.id) {
      dispatch(fetchUnreadCount(applicationData.id));
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
    }
  }, [applicationData?.id, dispatch]);

  // Helper functions
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "-";
    return moment(dateString).format("DD-MM-YYYY");
  };

  const formatYesNo = (value: string | undefined) => {
    if (!value) return "-";
    const lowerValue = value.toLowerCase();
    if (lowerValue === "y" || lowerValue === "yes") return "Yes";
    if (lowerValue === "n" || lowerValue === "no") return "No";
    return value;
  };

  const getStatusBadgeClass = (status: string | null | undefined, isDraft?: boolean) => {
    if (isDraft) return "badge bg-secondary-subtle text-secondary border border-secondary-subtle";
    if (!status) return "badge bg-secondary-subtle text-secondary border border-secondary-subtle";
    const code = status.toString().trim().toUpperCase();
    if (code === "D") return "badge bg-success-subtle text-success border border-success-subtle";
    return getGlobalStatusBadgeClass(code);
  };

  const getStatusText = (status: string | null | undefined, isDraft?: boolean) => {
    if (isDraft) return "Draft";
    if (!status) return "-";
    const code = status.toString().trim().toUpperCase();
    if (code === SapRequestStatus.APPROVED) return "Approved";
    if (code === "D") return "Approved";
    if (code === SapRequestStatus.REJECTED) return "Rejected";
    return getGlobalStatusLabel(code);
  };

  // Chat handlers
  const isBoilerplateDocMessage = (text: string) => {
    if (!text) return false;
    const lower = text.trim().toLowerCase();
    return (
      lower === "please share education documents" ||
      lower === "please share advertisement documents" ||
      lower === "please share other documents" ||
      (lower.startsWith("please share") && lower.endsWith("documents"))
    );
  };

  const handleSendMessage = async () => {
    const trimmedMsg = newMessage.trim();
    const isBoilerplate = isBoilerplateDocMessage(trimmedMsg);

    if (!trimmedMsg && selectedFiles.length === 0) {
      return;
    }

    // Mobile parity (StartChat.tsx):
    // Message is required ONLY when sending a direct attachment without a document type.
    if (selectedFiles.length > 0 && (!trimmedMsg || isBoilerplate)) {
      if (isBoilerplate) {
        setNewMessage("");
      }
      const errorMsg = "Please add a message along with the attachment.";
      setInputError(errorMsg);
      toast.error(errorMsg);
      setTimeout(() => messageInputRef.current?.focus(), 100);
      return;
    }

    setInputError(null);

    if (!applicationData?.id) {
      return;
    }

    try {
      setIsSending(true);
      let uploadedDocs: any[] = [];

      // If files are selected, upload them first
      if (selectedFiles.length > 0) {
        const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
        if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
          uploadedDocs = prepareUploadedAttachments(uploadResult.payload, selectedFiles);
        } else {
          toast.error("Failed to upload attached files. Please try again.");
          return;
        }
      }

      // Format message text with embedded attachment reference for backend @IsNotEmpty validator
      // Never send boilerplate share messages as the payload message with attachments
      const textToEmbed = isBoilerplate ? "" : trimmedMsg;
      const finalMessage = formatChatMessageText(textToEmbed, uploadedDocs);

      await dispatch(
        sendMessage({
          nocRequestId: applicationData.id,
          message: finalMessage,
          attachments: uploadedDocs,
          documents: uploadedDocs,
          files: selectedFiles,
        })
      ).unwrap();

      setNewMessage("");
      setSelectedFiles([]);
      setInputError(null);

      // Refresh messages after sending
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      if (uploadedDocs.length > 0) {
        dispatch(fetchChatAttachments({ nocRequestId: applicationData.id, nocType: "EX-INDIA" }));
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
      const errMsg =
        (typeof error === "string" && error.trim()) ||
        error?.message ||
        error?.error ||
        (Array.isArray(error?.data) && error.data[0]) ||
        "Error sending message. Please try again.";
      toast.error(errMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
          errors.push(
            `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(
              ", "
            )}`
          );
          return;
        }

        if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
          errors.push(
            `${file.name}: File size too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
          );
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        toast.error("File validation errors:\n" + errors.join("\n"));
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
        if (isBoilerplateDocMessage(newMessage)) {
          setNewMessage("");
        }
      }
    }

    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setInputError(null);
      return updated;
    });
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleChatToggle = () => {
    setShowChat(!showChat);
    if (!showChat && applicationData?.id) {
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      dispatch(fetchChatAttachments({ nocRequestId: applicationData.id, nocType: "NOC_EXINDIA" }));
    } else {
      dispatch(clearMessages());
      setSelectedFiles([]);
      setUploadingFiles(new Set());
    }
  };

  const formatMessageTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleAttachmentClick = async (rawAttachment: any) => {
    if (!rawAttachment) return;
    const opened = await openChatAttachment(rawAttachment, applicationData?.id, "NOC_EXINDIA", dispatch, attachments);
    if (!opened) {
      toast.error("Unable to open attachment. File link not found.");
    }
  };

  const handleDocumentClick = (docParam: any) => {
    handleDocumentClickUtil(docParam, "NOC_EXINDIA");
  };


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!applicationData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">
            <i className="ti ti-alert-circle me-2"></i>
            NOC request not found. Please go back to the main page and try
            again.
          </div>
          <Link to={routes.noc.foreignVisit} className="btn btn-primary">
            <i className="ti ti-arrow-left me-2"></i>
            Back to NOC List
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.foreignVisit}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                NOC for Ex-India (Foreign Visit) -{" "}
                {applicationData.referenceNumber || "N/A"}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.foreignVisit}>Foreign Visit</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Application Details
                </li>
              </ol>
            </nav>
          </div>

          {isApproved && (
            <button
              className="btn btn-success ms-auto"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              <i className="ti ti-download me-2"></i>
              {isDownloading ? "Downloading..." : "Download Digital NOC"}
            </button>
          )}
        </div>

        {/* Enhanced Chat Section - Moved to top */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Communication</h5>
              {(() => {
                const isApproverOrAdmin =
                  isCurrentUserApprover ||
                  (currentUserType &&
                    currentUserType.toUpperCase() !== "EMPLOYEE");
                const hasApproverMessage =
                  messages &&
                  messages.length > 0 &&
                  messages.some(
                    (m: any) =>
                      String(m.senderEmpId || "").replace(/^0+/, "") ===
                      String(
                        (applicationData as any)?.approverId ||
                        (applicationData as any)?.approverUserID ||
                        ""
                      ).replace(/^0+/, "") ||
                      m.senderType === "approver" ||
                      m.senderType === "admin"
                  );
                const hasAnyMessage = messages && messages.length > 0;
                const canChat =
                  isApproverOrAdmin || hasApproverMessage || hasAnyMessage;

                if (!canChat) {
                  return (
                    <div className="text-muted small">
                      <i className="ti ti-info-circle me-1"></i>
                      Chat will be available once the approver initiates communication
                    </div>
                  );
                }

                return (
                  <button
                    className="btn btn-primary btn-sm position-relative"
                    onClick={handleChatToggle}
                  >
                    <i className="ti ti-message me-1"></i>
                    {showChat
                      ? "Close Chat"
                      : messages.length > 0
                        ? "View Chat"
                        : "Start Chat"}
                    {unreadCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Main Application Details Card */}
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">Application Details</h4>
              <p className="text-muted mb-0 mt-1">
                Reference Number: {applicationData.referenceNumber || "-"}
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary fs-14">NOC Foreign Visit</span>
              <span
                className={`badge ${getStatusBadgeClass(requestStatusStr, applicationData.is_draft)} fs-14`}
              >
                {getGlobalStatusLabel(requestStatusStr)}
              </span>
            </div>
          </div>
          <div className="card-body">
            {/* Header info */}
            <h5 className="text-dark fw-medium mb-3">
              <i className="ti ti-user me-2"></i>
              Employee Information
            </h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employee Name</small>
                  <div className="fw-bold text-dark fs-15">{employeeName}</div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                  <div className="fw-bold text-primary fs-15">
                    {formatEmpIdForDisplay(
                      applicationData.empID ||
                      applicationData.empId ||
                      (applicationData as any).employee?.empId ||
                      (applicationData as any).employee?.empID ||
                      fallbackObj.empId ||
                      fallbackObj.empID ||
                      profileDetails.empId ||
                      profileDetails.empID ||
                      "-"
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Currently With (Approver)</small>
                  <div className="fw-bold text-dark fs-15">
                    {approverDisplay}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                  <div className="fw-bold text-dark fs-15">{designation}</div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                  <div className="fw-bold text-dark fs-15">{department}</div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining in DMRC</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(fallbackObj.date_of_joining || empObj.date_of_joining || appAny.date_of_joining)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employment Status</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(
                      fallbackObj.employmentType ||
                      empObj.employmentType ||
                      empDetails.employmentType ||
                      appAny.employee?.employmentType ||
                      appAny.employee?.details?.employmentType ||
                      appAny.employmentType ||
                      fallbackObj.empStatus ||
                      appAny.employmentStatus
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Pay Scale</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(fallbackObj.pay_scale || empDetails.pay_scale || appAny.pay_scale)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Contact Number</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(fallbackObj.phone || fallbackObj.mobileNumber || empObj.phone || appAny.contactNumber)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Email ID</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(fallbackObj.email || empObj.email || appAny.emailId)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Date of Birth</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(fallbackObj.birthdate || empObj.birthdate || appAny.birthdate || appAny.dateOfBirth)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Request Creation Date</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(applicationData.created_at || appAny.requestCreationDate)}
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {/* Visit Details */}
            <h5 className="text-dark fw-medium mb-3">
              <i className="ti ti-plane me-2"></i>
              Visit Details
            </h5>
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Place of Visit</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(
                      applicationData.placeOfVisitNoc ||
                      applicationData.placeOfVisit ||
                      applicationData.ePlaceOfVisitNoc
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Purpose of Visit</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(
                      applicationData.purposeOfVisit || applicationData.ePurposeOfVisit
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Period of Visit From</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(
                      applicationData.periodOfVisitFrom ||
                      applicationData.ePeriodOfVisitFrom
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Period of Visit To</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(
                      applicationData.periodOfVisitTo ||
                      applicationData.ePeriodOfVisitTo
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Total Members Travelling</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(applicationData.totalMembersTravel)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Leave Details</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDisplayValue(
                      applicationData.leaveDetails || applicationData.eLeaveDetails
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Expenditure Breakdown */}
            {(applicationData.stayingExpenditure ||
              applicationData.travelExpenditureA ||
              applicationData.otherExpenditureAm ||
              applicationData.totalExpenditure) && (
                <>
                  <hr className="my-4" />
                  <div className="mt-4 mb-3">
                    <h6 className="text-primary mb-3">💰 Expenditure Details</h6>
                  </div>

                  {applicationData.stayingExpenditure && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                        <span className="text-muted">Staying Expenditure Amount</span>
                        <span className="fw-medium">
                          ₹ {Number(applicationData.stayingExpenditure).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}

                  {applicationData.travelExpenditureA && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                        <span className="text-muted">Travel Expenditure Amount</span>
                        <span className="fw-medium">
                          ₹ {Number(applicationData.travelExpenditureA).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}

                  {applicationData.otherExpenditureAm && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                        <span className="text-muted">Other Expense</span>
                        <span className="fw-medium">
                          ₹ {Number(applicationData.otherExpenditureAm).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}

                  {applicationData.totalExpenditure && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2 bg-light p-2">
                        <span className="text-muted fw-bold">Total Expenditure</span>
                        <span className="fw-bold text-primary">
                          ₹ {Number(applicationData.totalExpenditure).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

            {/* Expenditure Borne By Details */}
            {(appAny.noc_travel || appAny.stay_list || applicationData.tExpenditureBorneBy || applicationData.sExpenditureBorneBy) && (
              <>
                <hr className="my-4" />
                <div className="mt-4 mb-3">
                  <h6 className="text-primary mb-3">📋 Expenditure Borne By</h6>
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                    <span className="text-muted">
                      Travel Expenditure Borne By
                    </span>
                    <span className="fw-medium">
                      {getTravelTitle(appAny.noc_travel?.travel_title || applicationData.tExpenditureBorneBy)}
                    </span>
                  </div>
                </div>

                {applicationData.tRelativesName && (
                  <>
                    <div className="mb-3 ms-4">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                        <span className="text-muted">Name</span>
                        <span className="fw-medium">{applicationData.tRelativesName}</span>
                      </div>
                    </div>
                    <div className="mb-3 ms-4">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                        <span className="text-muted">Relation</span>
                        <span className="fw-medium">{applicationData.tRelationWithPerson || "-"}</span>
                      </div>
                    </div>
                  </>
                )}

                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                    <span className="text-muted">
                      Stay Expenditure Borne By
                    </span>
                    <span className="fw-medium">
                      {getStayTitle(appAny.stay_list?.stay_title || applicationData.sExpenditureBorneBy)}
                    </span>
                  </div>
                </div>

                {applicationData.sRelativesName && (
                  <>
                    <div className="mb-3 ms-4">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                        <span className="text-muted">Name</span>
                        <span className="fw-medium">{applicationData.sRelativesName}</span>
                      </div>
                    </div>
                    <div className="mb-3 ms-4">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                        <span className="text-muted">Relation</span>
                        <span className="fw-medium">{applicationData.sRelationWithPerson || "-"}</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* D&AR and Vigilance Status */}
            <hr className="my-4" />
            <div className="mt-4 mb-3">
              <h6 className="text-primary mb-3">⚠️ D&AR and Vigilance Status</h6>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                <span className="text-muted">Whether Vigilance Pending</span>
                <span className="fw-medium">
                  {formatYesNo(applicationData.vigilancePending)}
                </span>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                <span className="text-muted">Whether D&AR Pending</span>
                <span className="fw-medium">
                  {formatYesNo(applicationData.dnARPending)}
                </span>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                <span className="text-muted">
                  Previously availed Ex-India leave during the last 5 years
                </span>
                <span className="fw-medium">
                  {applicationData?.travelEntries && applicationData.travelEntries.length > 0
                    ? "Yes"
                    : "No"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Travel Entries / Previous Visits */}
        {applicationData.travelEntries &&
          applicationData.travelEntries.length > 0 && (
            <div className="card mt-4">
              <div className="card-header">
                <h5 className="mb-0">
                  🌍 Previous Ex-India Visits (Last 5 Years) (
                  {applicationData.travelEntries.length})
                </h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Country Visited</th>
                        <th scope="col">Duration of Visit</th>
                        <th scope="col">Purpose of Visit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applicationData.travelEntries.map((entry, index) => (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className="ti ti-plane text-primary me-2"></i>
                              {entry.Seq || index + 1}
                            </div>
                          </td>
                          <td>
                            <div className="fw-medium">
                              {entry.Country || "-"}
                            </div>
                          </td>
                          <td>
                            <span className="text-muted">
                              {entry.DurationOfVisit || "-"}
                            </span>
                          </td>
                          <td>{entry.PurposeOfVisit || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        {/* Documents */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              📄 Uploaded Documents ({applicationData.documents?.length || 0})
            </h5>
          </div>
          <div className="card-body">
            <div className="alert alert-danger-light border border-danger rounded mb-3">
              <h6 className="text-danger fw-bold mb-2">
                <i className="ti ti-info-circle me-2"></i>
                Mandatory Attachments Instructions
              </h6>
              <div className="alert alert-info mb-3" role="alert">
                <ul className="mb-0" style={{ paddingLeft: "20px" }}>
                  <li className="mb-2">
                    - In case of expense are borne by employee himself/spouse,
                    proof of saving like last six months bank statement is to be
                    provided.
                  </li>
                  <li className="mb-2">
                    - In case of stay travel arrangement made by relative abroad,
                    the written declaration for the same is to be attached.
                  </li>
                  <li className="mb-2">
                    - In case of stay travel arrangement made by relative abroad,
                    proof of domicile is to be attached.
                  </li>
                  <li className="mb-0">
                    - Screenshot of approved/sanctioned leave requests for
                    ex-India must be attached.
                  </li>
                </ul>
              </div>
            </div>

            {applicationData.documents && applicationData.documents.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">File</th>
                      <th scope="col">Document Type</th>
                      <th scope="col">Upload Date</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applicationData.documents.map((doc, index) => (
                      <tr key={doc.id || index}>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="ti ti-file-text text-primary me-2"></i>
                            {index + 1}
                          </div>
                        </td>
                        <td>
                          <div className="fw-medium">
                            {doc.fileName?.split("/").pop() || "Document"}
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-info-transparent text-info">
                            {doc.typeOfAttachedDoc ||
                              doc.fileExtension ||
                              "Document"}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {formatDate((doc as any).created_at)}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDocumentClick(doc)}
                            title="View/Download File"
                          >
                            <i className="ti ti-eye me-1"></i>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted small">No documents uploaded.</div>
            )}

            <div className="alert alert-info-light border border-info rounded mt-3 mb-0">
              <p className="mb-0 text-muted small">
                <i className="ti ti-check me-2 text-info"></i>
                "It is certified that the expenditure has been or will be debited from this account statement only."
              </p>
            </div>
          </div>
        </div>

        {/* Remarks Summary */}
        {(appAny.remarks || appAny.hRemarks) && (
          <div className="card mt-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                <i className="ti ti-message me-2"></i>
                Remarks Summary
              </h5>
            </div>
            <div className="card-body">
              {appAny.remarks && (
                <div className="mb-3">
                  <span className="text-muted d-block fw-medium">Remarks:</span>
                  <p className="mb-0 mt-1 text-dark">{appAny.remarks}</p>
                </div>
              )}
              {appAny.hRemarks && (
                <div className="mb-0">
                  <span className="text-muted d-block fw-medium">HR / Approver Remarks:</span>
                  <p className={`mb-0 mt-1 ${appAny.requestStatus === "R" || appAny.status === "R" || isApproved === false
                    ? "text-danger fw-bold"
                    : "text-dark"
                    }`}>
                    {appAny.hRemarks}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Remarks History */}
        {(() => {
          let rawList: any[] = [
            ...(Array.isArray(appAny?.remarks_data) && appAny.remarks_data.length > 0
              ? appAny.remarks_data
              : Array.isArray(appAny?.history) && appAny.history.length > 0
                ? appAny.history
                : Array.isArray(appAny?.work_history) && appAny.work_history.length > 0
                  ? appAny.work_history
                  : Array.isArray(appAny?.remarksHistory) && appAny.remarksHistory.length > 0
                    ? appAny.remarksHistory
                    : [])
          ].map((item: any) => ({ ...item }));

          const applicantId = String(
            appAny?.empId ||
            appAny?.empID ||
            appAny?.employee?.empId ||
            appAny?.employee?.empID ||
            ""
          ).replace(/^0+/, "");

          const hasEmployeeEntry = rawList.some((item: any) => {
            const itemUserId = String(
              item?.approverUserID || item?.userId || item?.empID || ""
            ).replace(/^0+/, "");
            return (
              item?.usertype === "E" ||
              item?.usertype === "Employee" ||
              item?.approverName === "User" ||
              (applicantId && itemUserId && itemUserId === applicantId)
            );
          });

          const employeeRemarks = (() => {
            const candidates = [
              appAny?.anyRemarks,
              appAny?.any_remarks,
              appAny?.employeeRemarks,
              appAny?.empRemarks,
            ];
            for (const cand of candidates) {
              if (typeof cand === "string" && cand.trim() && cand.trim() !== "X" && cand.trim() !== "-" && cand.trim() !== "null") {
                return cand.trim();
              }
            }
            return "";
          })();

          const employeeTimestamp =
            appAny?.requestCreationDate ||
            appAny?.created_at ||
            appAny?.createdAt ||
            appAny?.sap_created_at ||
            appAny?.sapCreatedAt ||
            appAny?.appliedDate ||
            null;

          const employeeName = (() => {
            if (appAny?.employee) {
              const fname = appAny.employee.firstname || appAny.employee.firstName || "";
              const lname = appAny.employee.lsastname || appAny.employee.lastname || appAny.employee.lastName || "";
              const full = `${fname} ${lname}`.trim();
              if (full) return full;
            }
            return appAny?.employeeName || appAny?.empName || "Employee";
          })();

          const employeeUserId = appAny?.empId || appAny?.empID || appAny?.employee?.empId || "";

          if (hasEmployeeEntry) {
            rawList = rawList.map((item: any) => {
              const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
              const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
              if (isEmp && !item?.remarks && employeeRemarks) {
                return { ...item, remarks: employeeRemarks };
              }
              return { ...item };
            });
          } else if (appAny && (employeeUserId || employeeName !== "Employee" || appAny?.id || appAny?.referenceNumber)) {
            rawList.push({
              usertype: "Employee",
              approverUserID: employeeUserId,
              userId: employeeUserId,
              empID: employeeUserId,
              approverName: employeeName,
              name: employeeName,
              remarks: employeeRemarks,
              createdAt: employeeTimestamp,
              isEmployee: true,
            });
          }

          if (rawList.length === 0) return null;

          return (
            <div className="card mt-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="ti ti-history me-2"></i>
                  Previous Remarks History
                </h5>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column gap-3">
                  {rawList.map((item: any, index: number) => {
                    const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                    const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                    return (
                      <div
                        key={index}
                        className={`p-3 bg-white rounded border border-start-4 ${isEmp ? "border-start-info" : "border-start-primary"} shadow-xs`}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className={`avatar avatar-sm ${isEmp ? "bg-info-transparent text-info" : "bg-primary-transparent text-primary"} rounded-circle fw-bold`}>
                              <i className={`${isEmp ? "ti ti-user" : "ti ti-user-check"} fs-16`}></i>
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <h6 className="mb-0 text-dark fw-bold">
                                  {item.approverName ||
                                    item.history_approver?.name ||
                                    item.name ||
                                    (isEmp ? "Employee" : "Approver")}
                                </h6>
                                <span className={`badge ${isEmp ? "bg-info-subtle text-info" : "bg-warning-subtle text-warning"} border text-xs`}>
                                  {isEmp ? "Employee" : "Approver"}
                                </span>
                              </div>
                              <small className="text-muted">
                                ID:{" "}
                                {formatEmpIdForDisplay(
                                  item.approverUserID ||
                                  item.history_approver?.empId ||
                                  item.userId ||
                                  (isEmp ? employeeUserId : "")
                                ) || "-"}
                              </small>
                            </div>
                          </div>
                          <span className="badge bg-light text-muted border">
                            <i className="ti ti-calendar me-1"></i>
                            {formatDate(
                              item.createdAt ||
                              item.created_at ||
                              item.timeStamp
                            )}
                            {(item.createdAt || item.created_at || item.timeStamp) && !isNaN(new Date(item.createdAt || item.created_at || item.timeStamp).getTime())
                              ? " " + new Date(item.createdAt || item.created_at || item.timeStamp).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true,
                              }).toUpperCase()
                              : ""}
                          </span>
                        </div>
                        <div className="mt-2 pt-2 border-top">
                          <p className="mb-0 text-dark fw-medium fs-14">
                            {item.remarks ? (
                              <span>
                                <i className="ti ti-quote text-muted me-1"></i>
                                {item.remarks}
                              </span>
                            ) : (
                              <span className="text-muted fst-italic">No remarks</span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Declarations */}
        <div className="card mt-4 border-0 bg-light">
          <div className="card-body">
            <h6 className="fw-bold text-dark mb-2">
              <i className="ti ti-checkbox me-2 text-success"></i>
              Declaration
            </h6>
            <p className="mb-0 text-muted small">
              I certify that I am not involved in any court/police/vigilance case and there is no restriction placed by any authority on foreign travel.
            </p>
          </div>
        </div>

        {/* Enhanced Chat Drawer - Full Screen Height Right Side Panel */}
        {showChat && (
          <>
            {/* Soft click-away backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100"
              style={{
                zIndex: 99990,
                backgroundColor: "rgba(15, 23, 42, 0.35)",
                backdropFilter: "blur(2px)",
                transition: "opacity 0.2s ease",
              }}
              onClick={handleChatToggle}
            />

            {/* Chat Widget Docked to Right Side Panel */}
            <div
              className="position-fixed bg-white chat-corner-widget d-flex flex-column"
              style={{
                zIndex: 99999,
                top: 0,
                bottom: 0,
                right: 0,
                height: "100vh",
                width: isFullScreen ? "100vw" : "480px",
                maxWidth: "100vw",
                borderRadius: 0,
                borderLeft: "1px solid #e2e8f0",
                boxShadow: "-4px 0 24px rgba(15, 23, 42, 0.08)",
                transition: "width 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            >
              {/* Chat Header */}
              <div className="d-flex align-items-center justify-content-between px-3.5 py-3 border-bottom bg-white flex-shrink-0 chat-widget-header">
                <div className="d-flex align-items-center gap-2.5">
                  <div className="position-relative flex-shrink-0">
                    <div
                      className="avatar rounded-2 d-flex align-items-center justify-content-center fw-bold shadow-xs"
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "6px",
                        background: "linear-gradient(135deg, #f26522 0%, #d94e0c 100%)",
                        color: "#fff",
                      }}
                    >
                      <i className="ti ti-messages fs-18"></i>
                    </div>
                    <span
                      className="position-absolute bottom-0 end-0 bg-success border border-2 border-white"
                      style={{ width: "9px", height: "9px", borderRadius: "2px" }}
                      title="Active"
                    />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0 fw-bold text-dark fs-14">Chat - Foreign Visit</h6>
                      <span className="badge bg-success-subtle text-success fs-10 px-2 py-0.5 rounded-1 fw-semibold">
                        Active
                      </span>
                    </div>
                    <small className="text-muted fs-11 d-block text-truncate" style={{ maxWidth: "230px" }}>
                      Ref: #{applicationData.referenceNumber || applicationData.id}
                      {applicationData.empID ? ` • Emp: ${applicationData.empID}` : ""}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-1.5">
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={() => setIsFullScreen(!isFullScreen)}
                    title={isFullScreen ? "Restore to Side Panel" : "Expand to Full Screen"}
                  >
                    <i className={`ti ${isFullScreen ? "ti-arrows-minimize" : "ti-arrows-maximize"} fs-14`}></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={() =>
                      dispatch(
                        fetchMessages({ nocRequestId: applicationData.id })
                      )
                    }
                    disabled={chatLoading}
                    title="Refresh Messages"
                  >
                    <i className={`ti ti-refresh fs-14 ${chatLoading ? "ti-spin text-primary" : ""}`}></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={handleChatToggle}
                    title="Close Chat"
                  >
                    <i className="ti ti-x fs-16"></i>
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="chat-messages-container p-3 flex-grow-1 overflow-auto" style={{ backgroundColor: "#f8fafc" }}>
                {(chatError || uploadError) && (
                  <div className="alert alert-danger alert-dismissible py-2 px-3 fs-12 mb-3 rounded-2 shadow-xs">
                    <button
                      type="button"
                      className="btn-close fs-10"
                      onClick={() => {
                        dispatch(clearError());
                        dispatch(clearUploadError());
                      }}
                    ></button>
                    {chatError || uploadError}
                  </div>
                )}

                {chatLoading && messages.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted small mt-2 fs-12">Loading conversation...</p>
                  </div>
                ) : messages.length === 0 ? (
                  /* Mobile Empty State (Screenshot 3) */
                  <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center my-auto">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: "72px",
                        height: "72px",
                        backgroundColor: "#f8fafc",
                        border: "1.5px solid #e2e8f0",
                        color: "#94a3b8",
                      }}
                    >
                      <i className="ti ti-messages" style={{ fontSize: "36px" }}></i>
                    </div>
                    <h6 className="fw-bold mb-1" style={{ color: "#64748b", fontSize: "15px" }}>
                      No messages yet
                    </h6>
                    <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                      Start the conversation below
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="text-center my-2">
                      <span
                        className="badge px-3 py-1 text-muted"
                        style={{
                          backgroundColor: "rgba(0,0,0,0.05)",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: 500,
                        }}
                      >
                        Today
                      </span>
                    </div>
                    {messages.map((msg: ChatMessage, index: number) => {
                      const isOwn = isMyMessage(msg);
                      const rawMsg = msg.message || "";
                      const paperclipIdx = rawMsg.indexOf("📎");
                      const hasPaperclip = paperclipIdx !== -1;
                      const displayMsgText = hasPaperclip
                        ? rawMsg.substring(0, paperclipIdx).trim()
                        : rawMsg.trim();
                      let extractedAttNames: string[] = [];
                      if (hasPaperclip) {
                        const attPart = rawMsg.substring(paperclipIdx).replace(/^[📎\s]+/, "").trim();
                        extractedAttNames = attPart.split(",").map((s: string) => s.trim()).filter(Boolean);
                      }
                      const attachmentsList: any[] =
                        msg.documents && msg.documents.length > 0
                          ? msg.documents
                          : msg.attachments && msg.attachments.length > 0
                            ? msg.attachments
                            : extractedAttNames.length > 0
                              ? extractedAttNames
                              : [];

                      return (
                        <div
                          key={msg.id || index}
                          className={`d-flex mb-3.5 ${isOwn ? "justify-content-end" : "justify-content-start"}`}
                        >
                          {/* Approver/Participant Avatar */}
                          {!isOwn && (
                            <div className="me-2 flex-shrink-0 align-self-end mb-0.5">
                              <EmployeeAvatar
                                empName={msg.senderName || "Approver"}
                                empImage={msg.senderImage}
                                empId={msg.senderEmpId}
                                size="sm"
                                shape="rounded"
                                showName={false}
                                showEmpId={false}
                                className="rounded-2 shadow-xs"
                              />
                            </div>
                          )}

                          <div
                            className="position-relative"
                            style={{ maxWidth: "84%", wordBreak: "break-word" }}
                          >
                            {/* Sender Label Above Bubble */}
                            <div
                              className={`d-flex align-items-center gap-1.5 mb-1 px-1 ${isOwn ? "justify-content-end" : "justify-content-start"
                                }`}
                            >
                              <span className="fw-semibold text-dark fs-11">
                                {isOwn ? "You" : (msg.senderName || "Approver")}
                              </span>
                              <span
                                className={`badge fs-9 px-1.5 py-0.5 text-uppercase rounded-1 fw-semibold ${isOwn
                                  ? "bg-primary-subtle text-primary"
                                  : msg.senderType === "approver"
                                    ? "bg-warning-subtle text-warning-emphasis"
                                    : "bg-secondary-subtle text-secondary"
                                  }`}
                              >
                                {msg.senderType || (isOwn ? "employee" : "approver")}
                              </span>
                            </div>

                            <div
                              className="p-2.5 shadow-sm position-relative"
                              style={{
                                minWidth: "120px",
                                borderRadius: isOwn ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                                backgroundColor: isOwn ? "#c82333" : "#ffffff",
                                color: isOwn ? "#ffffff" : "#1e293b",
                                border: isOwn ? "none" : "1px solid #e2e8f0",
                              }}
                            >
                              {msg.documentType && (
                                <div className="mb-2">
                                  <span
                                    className={`badge fs-10 fw-medium px-2 py-1 rounded-1 ${isOwn
                                      ? "bg-white bg-opacity-25 text-white"
                                      : "bg-warning-subtle text-warning-emphasis border border-warning"
                                      }`}
                                  >
                                    <i className="ti ti-file-certificate me-1"></i>
                                    Requested: {msg.documentType}
                                  </span>
                                </div>
                              )}

                              {displayMsgText && (
                                <p
                                  className="mb-0 chat-message-content"
                                  style={{
                                    fontSize: "13.5px",
                                    lineHeight: "1.5",
                                    color: isOwn ? "#ffffff" : "#1e293b",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {displayMsgText}
                                </p>
                              )}

                              {/* Render Interactive Attachments / Images */}
                              {attachmentsList.length > 0 && (
                                <div
                                  className={`d-flex flex-column gap-2 ${displayMsgText ? "mt-2.5 pt-2 border-top" : ""
                                    }`}
                                  style={{
                                    borderColor: isOwn ? "rgba(255, 255, 255, 0.25)" : "#f1f5f9",
                                  }}
                                >
                                  {attachmentsList.map((attachment: any, attIdx: number) => {
                                    const rawName =
                                      typeof attachment === "string"
                                        ? attachment
                                        : attachment.fileName ||
                                        attachment.name ||
                                        attachment.FileName ||
                                        "Attachment";
                                    const fileName =
                                      rawName.split("/").pop()?.split("-").slice(2).join("-") ||
                                      rawName.split("/").pop() ||
                                      "Attachment";
                                    const directUrl =
                                      typeof attachment === "object"
                                        ? attachment.url
                                        : rawName.startsWith("http")
                                          ? rawName
                                          : undefined;
                                    const isImg =
                                      /\.(jpg|jpeg|png|webp|gif)$/i.test(fileName) ||
                                      (typeof attachment === "object" &&
                                        attachment.contentType?.startsWith("image/"));
                                    const isPdf =
                                      /\.pdf$/i.test(fileName) ||
                                      (typeof attachment === "object" &&
                                        attachment.contentType === "application/pdf");
                                    const fileSize =
                                      typeof attachment === "object" ? attachment.fileSize : undefined;

                                    return (
                                      <div key={attIdx} className="d-flex flex-column gap-1">
                                        {/* Inline image preview if direct url exists */}
                                        {isImg && directUrl && (
                                          <div
                                            className="overflow-hidden rounded-2 border mb-1 cursor-pointer position-relative shadow-xs"
                                            style={{
                                              maxHeight: "190px",
                                              maxWidth: "280px",
                                              borderRadius: "6px",
                                              backgroundColor: "#000",
                                            }}
                                            onClick={() => handleAttachmentClick(attachment)}
                                            title="Click to view full image"
                                          >
                                            <img
                                              src={directUrl}
                                              alt={fileName}
                                              className="w-100 h-100 object-fit-cover"
                                              style={{
                                                maxHeight: "190px",
                                                display: "block",
                                                cursor: "pointer",
                                              }}
                                            />
                                          </div>
                                        )}

                                        {/* Mobile-styled Attachment Card (Screenshot 4) */}
                                        <div
                                          className="d-flex align-items-center justify-content-between p-2 mt-1.5 cursor-pointer"
                                          onClick={() => handleAttachmentClick(attachment)}
                                          style={{
                                            minWidth: "200px",
                                            maxWidth: "280px",
                                            borderRadius: "10px",
                                            border: isOwn ? "1px solid rgba(255,255,255,0.2)" : "1px solid #e2e8f0",
                                            backgroundColor: isOwn ? "rgba(255, 255, 255, 0.15)" : "#f8fafc",
                                            transition: "background-color 0.15s ease",
                                          }}
                                          title="Tap to open attachment"
                                        >
                                          <div className="d-flex align-items-center gap-2.5 overflow-hidden me-2">
                                            <div
                                              className="d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                              style={{
                                                width: "36px",
                                                height: "36px",
                                                borderRadius: "8px",
                                                backgroundColor: isPdf ? "#FF5252" : isImg ? "#2196F3" : "#64748b",
                                              }}
                                            >
                                              <i
                                                className={isPdf ? "ti ti-file-text" : isImg ? "ti ti-photo" : "ti ti-paperclip"}
                                                style={{ fontSize: "18px" }}
                                              ></i>
                                            </div>
                                            <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                              <div
                                                className={`fw-bold text-truncate ${isOwn ? "text-white" : "text-dark"}`}
                                                style={{ fontSize: "12.5px", lineHeight: "1.3" }}
                                                title={fileName}
                                              >
                                                {fileName}
                                              </div>
                                              <small
                                                className={`d-block ${isOwn ? "text-white-50" : "text-muted"}`}
                                                style={{ fontSize: "10.5px", marginTop: "2px" }}
                                              >
                                                {isPdf ? "PDF" : isImg ? "IMAGE" : "FILE"} • Tap to open
                                              </small>
                                            </div>
                                          </div>
                                          <div className="flex-shrink-0 pe-1">
                                            <i className={`ti ti-chevron-right fs-15 ${isOwn ? "text-white-50" : "text-muted"}`}></i>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Timestamp */}
                              <div className="d-flex align-items-center justify-content-end gap-1.5 mt-1.5">
                                <small
                                  style={{
                                    fontSize: "10px",
                                    color: isOwn ? "rgba(255, 255, 255, 0.85)" : "#94a3b8",
                                  }}
                                >
                                  {formatMessageTime(msg.createdAt)}
                                </small>
                                {isOwn && <i className="ti ti-checks fs-11 text-white-50"></i>}
                              </div>
                            </div>
                          </div>

                          {/* Employee Avatar */}
                          {isOwn && (
                            <div className="ms-2 flex-shrink-0 align-self-end mb-0.5">
                              <EmployeeAvatar
                                empName={
                                  msg.senderName ||
                                  applicationData.employeeName ||
                                  "User"
                                }
                                empImage={msg.senderImage || (applicationData as any).employeeDetails?.image}
                                empId={msg.senderEmpId || applicationData.empID}
                                size="sm"
                                shape="rounded"
                                showName={false}
                                showEmpId={false}
                                className="rounded-2 shadow-xs"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <div className="chat-input-container bg-white border-top p-3 flex-shrink-0">
                {/* File Attachments Preview - Mobile Preview Card (Screenshot 2) */}
                {selectedFiles.length > 0 && (
                  <div className="mb-2">
                    {selectedFiles.map((file, index) => {
                      const ext = file.name.split(".").pop()?.toLowerCase() || "";
                      const isImg = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);
                      const isPdf = ext === "pdf";
                      const fileSizeKB = Math.round(file.size / 1024);

                      return (
                        <div
                          key={index}
                          className="d-flex align-items-center justify-content-between p-2 mb-1"
                          style={{
                            backgroundColor: "#f8fafc",
                            border: "1.5px solid #e2e8f0",
                            borderRadius: "14px",
                          }}
                        >
                          <div
                            className="d-flex align-items-center overflow-hidden flex-grow-1"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              const blobUrl = URL.createObjectURL(file);
                              window.open(blobUrl, "_blank");
                            }}
                            title="Tap to preview"
                          >
                            <div
                              className="d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{
                                width: "42px",
                                height: "42px",
                                backgroundColor: isPdf ? "#FF5252" : isImg ? "#2196F3" : "#64748b",
                                borderRadius: "10px",
                                color: "#ffffff",
                              }}
                            >
                              <i
                                className={isPdf ? "ti ti-file-text" : isImg ? "ti ti-photo" : "ti ti-paperclip"}
                                style={{ fontSize: "22px" }}
                              ></i>
                            </div>
                            <div className="overflow-hidden me-2" style={{ minWidth: 0 }}>
                              <div
                                className="fw-bold text-truncate text-dark"
                                style={{ fontSize: "13.5px", lineHeight: "1.3" }}
                                title={file.name}
                              >
                                {file.name}
                              </div>
                              <div
                                className="text-muted text-truncate"
                                style={{ fontSize: "11px", marginTop: "2px" }}
                              >
                                {ext.toUpperCase()} • {fileSizeKB}KB • Ready — Tap to preview
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            onClick={() => removeFile(index)}
                            style={{
                              width: "26px",
                              height: "26px",
                              backgroundColor: "#94a3b8",
                              color: "#ffffff",
                              border: "none",
                              padding: 0,
                            }}
                            title="Remove attachment"
                          >
                            <i className="ti ti-x" style={{ fontSize: "14px" }}></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Input Error Message Banner (Direct Attachment Requires Message) */}
                {inputError && (
                  <div className="mb-2 flex-shrink-0">
                    <div
                      className="d-flex align-items-center justify-content-between p-2 px-2.5 rounded-3 border shadow-xs"
                      style={{
                        backgroundColor: "#fef2f2",
                        borderColor: "#fecaca",
                        color: "#b91c1c",
                        fontSize: "12.5px",
                        fontWeight: 500,
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ti ti-alert-circle fs-16 flex-shrink-0" style={{ color: "#dc2626" }}></i>
                        <span>{inputError}</span>
                      </div>
                      <button
                        type="button"
                        className="btn p-0 border-0 bg-transparent text-danger d-flex align-items-center ms-2"
                        onClick={() => setInputError(null)}
                        title="Dismiss"
                      >
                        <i className="ti ti-x fs-12"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Chat Input Bar - Pill Container (Screenshots 2 & 3) */}
                <div
                  className="d-flex align-items-center px-2 py-1"
                  style={{
                    border: inputError ? "1.5px solid #f87171" : "1.5px solid #e2e8f0",
                    borderRadius: "28px",
                    backgroundColor: "#ffffff",
                    minHeight: "46px",
                    transition: "border-color 0.2s ease",
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelection}
                    multiple
                    className="d-none"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  />

                  <button
                    type="button"
                    className="btn btn-link p-0 text-muted ms-2 me-1 d-flex align-items-center justify-content-center flex-shrink-0"
                    onClick={triggerFileInput}
                    disabled={uploading || isSending}
                    style={{ width: "32px", height: "32px", textDecoration: "none" }}
                    title="Attach files"
                  >
                    {uploading ? (
                      <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                    ) : (
                      <i className="ti ti-paperclip" style={{ fontSize: "20px", color: "#64748b" }}></i>
                    )}
                  </button>

                  <input
                    ref={messageInputRef}
                    type="text"
                    className="form-control border-0 shadow-none px-2"
                    style={{ fontSize: "13.5px", backgroundColor: "transparent" }}
                    placeholder="Start Typing..."
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      if (inputError) setInputError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={isSending}
                  />

                  <button
                    className="btn rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 ms-1"
                    onClick={handleSendMessage}
                    disabled={isSending || uploading || (!newMessage.trim() && selectedFiles.length === 0)}
                    style={{
                      width: "36px",
                      height: "36px",
                      backgroundColor: (newMessage.trim() || selectedFiles.length > 0) ? "#c82333" : "#cbd5e1",
                      border: "none",
                      cursor: (newMessage.trim() || selectedFiles.length > 0) ? "pointer" : "default",
                      transition: "background-color 0.2s ease",
                    }}
                    title="Send message"
                  >
                    {isSending || uploading ? (
                      <div className="spinner-border spinner-border-sm text-white" role="status" />
                    ) : (
                      <i className="ti ti-send text-white" style={{ fontSize: "16px", marginLeft: "2px" }}></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Media / Lightbox Preview Modal */}
        {previewMedia && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center"
            style={{ zIndex: 1080, backdropFilter: "blur(4px)" }}
            onClick={() => setPreviewMedia(null)}
          >
            <div
              className="bg-white rounded-2 shadow-lg p-3.5 position-relative"
              style={{ maxWidth: "90vw", maxHeight: "90vh", minWidth: "320px", borderRadius: "8px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                <h6 className="mb-0 fw-bold text-dark text-truncate fs-14" style={{ maxWidth: "70%" }}>
                  <i className="ti ti-photo me-1.5 text-primary"></i>
                  {previewMedia.title}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <a
                    href={previewMedia.url}
                    target="_blank"
                    rel="noreferrer"
                    download={previewMedia.title}
                    className="btn btn-sm btn-outline-primary py-1 px-2.5 fs-12 rounded-2"
                    style={{ borderRadius: "6px" }}
                  >
                    <i className="ti ti-download me-1"></i>Download
                  </a>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setPreviewMedia(null)}
                  ></button>
                </div>
              </div>
              <div className="text-center overflow-auto p-2" style={{ maxHeight: "calc(90vh - 100px)" }}>
                {previewMedia.isImage ? (
                  <img
                    src={previewMedia.url}
                    alt={previewMedia.title}
                    className="img-fluid rounded-2"
                    style={{ maxHeight: "75vh", objectFit: "contain", borderRadius: "6px" }}
                  />
                ) : (
                  <iframe
                    src={previewMedia.url}
                    title={previewMedia.title}
                    style={{ width: "80vw", height: "70vh", border: "none" }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <style>
          {`
            @keyframes chatSlideIn {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            
            .chat-corner-widget {
              animation: chatSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
            }
            
            @media (max-width: 576px) {
              .chat-corner-widget {
                bottom: 0 !important;
                right: 0 !important;
                left: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                height: 85vh !important;
                max-height: 85vh !important;
                border-radius: 8px 8px 0 0 !important;
              }
            }
            
            .chat-bubble-incoming {
              border-radius: 6px !important;
              border: 1px solid #e2e8f0;
              background-color: #ffffff;
              box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
            }

            .chat-bubble-outgoing {
              border-radius: 6px !important;
              background: linear-gradient(135deg, #f26522 0%, #d94e0c 100%);
              box-shadow: 0 2px 8px rgba(242, 101, 34, 0.22);
            }

            .chat-header-btn:hover {
              background-color: #f1f5f9 !important;
              color: #0f172a !important;
            }

            .chat-attach-btn:hover {
              background-color: #e2e8f0 !important;
            }

            .chat-send-btn:hover:not(:disabled) {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(242, 101, 34, 0.35) !important;
            }

            .chat-input-field:focus {
              background-color: #ffffff !important;
              border-color: #f26522 !important;
              box-shadow: 0 0 0 3px rgba(242, 101, 34, 0.15) !important;
            }

            .chat-messages-container {
              flex: 1;
              overflow-y: auto;
              min-height: 0;
            }
            
            .chat-input-container {
              flex-shrink: 0;
            }
            
            .chat-att-card-in:hover {
              background-color: #f1f5f9 !important;
              border-color: #cbd5e1 !important;
            }

            .chat-att-card-out:hover {
              background-color: rgba(255, 255, 255, 0.26) !important;
              border-color: rgba(255, 255, 255, 0.38) !important;
            }

            .chat-message-content {
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .file-attachment-preview {
              max-width: 100%;
              overflow-x: auto;
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default ForeignVisitDetails;
